import { computed, makeObservable, observable } from 'mobx';

import { CustomGradingService } from '@/customGrading/services/CustomGradingService';
import { SetupPresetType } from '@/media/entity/SetupPreset';
import Diamond from '@/product/entity/Diamond';
import { ProductId } from '@/product/entity/Product';

import { CustomGradeCode } from '../constants/customGradeCodes';

import GradeDictEntry, { GradeDictEntryId } from './GradeDictEntry';

export class GradeAttributeOption {
  code!: CustomGradeCode;
  spId!: SetupPresetType[];
  title!: string;
}

export class GradeAttributeModel extends GradeAttributeOption {
  dict!: GradeDictEntry[];
}

export default class GradeAttribute extends GradeAttributeModel {
  dict: GradeDictEntry[];

  basicDict: GradeDictEntry[];

  extraDict: GradeDictEntry[];

  // "dictMap" contains map of full dict array,
  // it includes "basicDict" and "extraDict"
  dictMap: Record<string, GradeDictEntry[]> = {};

  spId: GradeAttributeModel['spId'];

  title: GradeAttributeModel['title'];

  constructor({ code, dict, spId, title }: GradeAttributeModel) {
    super();

    makeObservable(this, {
      dict: observable,
      basicDict: observable,
      extraDict: observable,
      dictMap: observable,
      spId: observable,
      title: observable,
      dictEntriesWithReferences: computed,
      dictEntriesByReferenceMap: computed,
      referencesIds: computed,
      maxDictEntryTitleLength: computed,
      minDistanceBetweenReferences: computed,
      maxGroupLength: computed,
    });

    this.code = code;
    this.dict = dict ? dict.map((d) => new GradeDictEntry(d, code)) : [];
    this.spId = spId;
    this.title = title;

    this.dictMap = this.getDictMap(this.dict);
    const [basicDict, extraDict] = this.getBasicDict();

    this.basicDict = basicDict;
    this.extraDict = extraDict;
  }

  get dictEntriesWithReferences() {
    return this.dict.filter((d) => !!d.refId) as GradeDictEntry[];
  }

  get dictEntriesByReferenceMap() {
    return this.dictEntriesWithReferences.reduce<Record<ProductId, GradeDictEntry>>((result, dictEntry) => {
      result[dictEntry.refId!] = dictEntry;

      return result;
    }, {});
  }

  get referencesIds() {
    return this.dictEntriesWithReferences.map((d) => d.refId) as number[];
  }

  get maxDictEntryTitleLength() {
    let maxTitleLength = 2;

    this.dict.forEach((d) => {
      if (d.title.length > maxTitleLength) {
        maxTitleLength = d.title.length;
      }
    });

    return maxTitleLength;
  }

  get minDistanceBetweenReferences() {
    let reference: Nullable<number> = null;
    let minDistance = 3;
    let distance = 1;

    this.dict.forEach((d) => {
      if (reference === null) {
        if (!d.refId) {
          return;
        }

        reference = d.refId;

        return;
      }

      if (!d.refId) {
        distance++;

        return;
      }

      if (d.refId && reference !== d.refId) {
        if (distance < minDistance) {
          minDistance = distance;
        }

        distance = 1;
      }
    });

    return minDistance;
  }

  get maxGroupLength() {
    let length = 1;

    Object.keys(this.dictMap).forEach((title) => {
      const subgradesLength = this.dictMap[title].length;

      if (subgradesLength > length) {
        length = subgradesLength;
      }
    });

    return length;
  }

  getDictEntryById(id: GradeDictEntryId) {
    return this.dict?.find((d) => d.id === id) ?? null;
  }

  getManualBracketingValue(product: Diamond) {
    return CustomGradingService.getManualBracketing(this.code, product, this.dict);
  }

  getSupplierGradeValue(product: Diamond) {
    return CustomGradingService.getSupplierGrade(product, this.dict);
  }

  getCutwiseAutogradeValue(product: Diamond, references: Diamond[]) {
    return CustomGradingService.getCutwiseAutograde(this.code, product, references, this.dict);
  }

  getInitialGrade(product: Diamond, references: Diamond[]) {
    return CustomGradingService.getInitialGrade(this.code, product, references, this.dict, this.basicDict);
  }

  getDictMap(dict: GradeDictEntry[]) {
    return CustomGradingService.dictToMap(dict);
  }

  private getBasicDict() {
    // copy array into new variable for immutability
    const fullDict = this.dict.slice();

    let lastDictEntryWithRefIndex: Nullable<number> = null;

    // iterate through array from last to first to find last element with reference
    for (let i = fullDict.length - 1; i >= 0; i--) {
      const dictEntry = fullDict[i];

      if (lastDictEntryWithRefIndex === null && dictEntry.refId) {
        // if grade is subgrade we need to find last grade in group
        // lets say we have group M- M M+ and only M- has reference, we need to find index of M+
        if (dictEntry.isSubgrade) {
          const group = this.dictMap[dictEntry.group];
          const lastInGroup = group[group.length - 1];

          const targetIndex = fullDict.findIndex(({ id }) => id === lastInGroup.id);

          if (targetIndex > -1) {
            lastDictEntryWithRefIndex = targetIndex;

            break;
          }
        } else {
          lastDictEntryWithRefIndex = i;

          break;
        }
      }
    }

    if (lastDictEntryWithRefIndex === null) {
      return [fullDict, []];
    }

    // split initial array to basic (without grades line N, P, O, etc)
    const extraDict = fullDict.splice(lastDictEntryWithRefIndex + 1);

    return [fullDict, extraDict];
  }
}
